import { Injectable, inject } from '@angular/core';
import { map, type Observable } from 'rxjs';
import { EducationalWorkflowConfigService } from '../educational-workflow-config.service';
import type { MergedEducationalArtifactConfig } from '../educational-workflow-config.types';
import { fuzzyPickOption } from '../utils/fuzzy-match.util';
import { normalizeSlotText } from '../utils/slot-normalizer.util';
import type { WorkflowSlotSnapshot } from '../models/conversation-state.model';

/** Static option shape from shared-slots.config.json */
export interface SlotOptionJson {
  label: string;
  value: string;
  aliases?: string[];
}

@Injectable({
  providedIn: 'root',
})
export class AssistantSlotFillingService {
  private readonly config = inject(EducationalWorkflowConfigService);

  extractFromMessage(
    text: string,
    merged: MergedEducationalArtifactConfig,
    domains: ReadonlyArray<{ id: number; name: string }>,
    questions: ReadonlyArray<{ id: number; content: string }>
  ): Partial<WorkflowSlotSnapshot> {
    const patch: Partial<WorkflowSlotSnapshot> = {};
    const slots = (merged.shared?.slots ?? {}) as Record<string, unknown>;
    const t = normalizeSlotText(text);

    const ped = slots['pedagogy'] as { options?: SlotOptionJson[] } | undefined;
    if (ped?.options?.length) {
      const hit = fuzzyPickOption(text, ped.options);
      if (hit) patch.pedagogy = hit;
    }

    const lvl = slots['standardLevel'] as { options?: SlotOptionJson[] } | undefined;
    if (lvl?.options?.length) {
      const hit = fuzzyPickOption(text, lvl.options);
      if (hit) patch.standardLevel = hit;
    }

    const ctry = slots['countryRegion'] as { options?: SlotOptionJson[] } | undefined;
    if (ctry?.options?.length) {
      const hit = fuzzyPickOption(text, ctry.options.filter(o => o.value !== ''));
      if (hit) patch.countryRegion = hit;
    }

    if (domains.length) {
      let best: { id: number; name: string } | null = null;
      let bestScore = 999;
      for (const d of domains) {
        const n = normalizeSlotText(d.name);
        if (!n) continue;
        if (t.includes(n) || n.includes(t)) {
          best = d;
          bestScore = 0;
          break;
        }
        const dist = levenshteinShort(t, n, 3);
        if (dist < bestScore) {
          bestScore = dist;
          best = d;
        }
      }
      if (best && bestScore <= 2) {
        patch.topicDomain = { id: best.id, name: best.name };
      }
    }

    if (questions.length) {
      let bestQ: { id: number; content: string } | null = null;
      let bestScore = 999;
      for (const q of questions) {
        const c = normalizeSlotText(String(q.content));
        if (!c) continue;
        if (t.includes(c) || c.includes(t)) {
          bestQ = { id: q.id, content: String(q.content) };
          bestScore = 0;
          break;
        }
        const dist = levenshteinShort(t, c, 4);
        if (dist < bestScore) {
          bestScore = dist;
          bestQ = { id: q.id, content: String(q.content) };
        }
      }
      if (bestQ && bestScore <= 2) {
        patch.learningFocus = bestQ;
      }
    }

    return patch;
  }

  loadMerged(artifact: string): Observable<MergedEducationalArtifactConfig | null> {
    return this.config.getArtifactConfig(artifact);
  }

  loadAllArtifactKeywords(): Observable<Map<string, string[]>> {
    return this.config.ensureLoaded().pipe(
      map(bundle => {
        const m = new Map<string, string[]>();
        for (const [key, art] of Object.entries(bundle.artifacts)) {
          const words = [
            ...(art.intents ?? []),
            ...(art.aliases ?? []),
            key,
          ].map(w => normalizeSlotText(w));
          m.set(key, [...new Set(words.filter(Boolean))]);
        }
        return m;
      })
    );
  }
}

function levenshteinShort(a: string, b: string, cap: number): number {
  if (a === b) return 0;
  const m = a.length;
  const n = b.length;
  if (Math.abs(m - n) > cap) return cap + 1;
  const row = new Array<number>(n + 1);
  for (let j = 0; j <= n; j++) row[j] = j;
  for (let i = 1; i <= m; i++) {
    let prev = row[0];
    row[0] = i;
    let rowMin = row[0];
    for (let j = 1; j <= n; j++) {
      const tmp = row[j];
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      row[j] = Math.min(row[j] + 1, row[j - 1] + 1, prev + cost);
      prev = tmp;
      if (row[j] < rowMin) rowMin = row[j];
    }
    if (rowMin > cap) return cap + 1;
  }
  return row[n]!;
}
